import type { JSX } from 'react';
import {
  VNC_ENCODING_PREFERENCES,
  VNC_SERVER_KINDS,
  type NetworkNode,
  type NodeVnc,
  type VncEncodingPreference,
  type VncServerKind,
} from '@netdeck/contracts';
import { Button } from '../../shared/ui/Button';
import { CollapsibleSection } from '../../shared/ui/CollapsibleSection';
import { canHostVnc } from './access';
import { formatMoment } from '../../shared/format/time';
import { currentLanguage, useTranslation } from '../../shared/i18n';
import { messageText } from '../../shared/i18n/message';
import { useErrorText } from '../../shared/i18n/useErrorText';
import { NodeCredentials } from './NodeCredentials';
import { useNodeVnc, useProbeVnc, useUpdateNodeVnc } from './api';

const KIND_LABEL_KEYS: Readonly<Record<VncServerKind, string>> = {
  auto: 'vnc.serverKind.auto',
  tightvnc: 'vnc.serverKind.tightvnc',
  ultravnc: 'vnc.serverKind.ultravnc',
  tigervnc: 'vnc.serverKind.tigervnc',
  realvnc: 'vnc.serverKind.realvnc',
  x11vnc: 'vnc.serverKind.x11vnc',
  other: 'vnc.serverKind.other',
};

const ENCODING_LABEL_KEYS: Readonly<Record<VncEncodingPreference, string>> = {
  tight: 'vnc.encoding.tight',
  zrle: 'vnc.encoding.zrle',
  hextile: 'vnc.encoding.hextile',
};

/**
 * VNC в панели узла (§5.1, задача 12).
 *
 * Здесь всё, что относится к подключению именно к этой машине: кнопка,
 * параметры, учётные данные и результат зондирования. Раздельные экраны
 * для этого не годятся — инженер, у которого не подключается одна машина,
 * должен видеть про неё всё в одном месте.
 */
export function VncSection({
  node,
  open,
  onToggle,
  canConnect,
  canWrite,
  canManageCredentials,
  onConnect,
}: {
  node: NetworkNode;
  /** Раскрыт ли раздел. Решает это панель: раскрытым может быть только один. */
  open: boolean;
  onToggle: () => void;
  canConnect: boolean;
  canWrite: boolean;
  canManageCredentials: boolean;
  onConnect: (node: { id: string; name: string }) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const settings = useNodeVnc(node.id);
  const update = useUpdateNodeVnc(node.id);
  const probe = useProbeVnc(node.id);

  // Типы, у которых рабочего стола не бывает, и всё, у чего нет адреса,
  // подключать некуда — раздела VNC у таких узлов нет вовсе.
  if (!canHostVnc(node.kind) || (node.address ?? '').trim().length === 0) {
    return null;
  }

  const vnc = settings.data;

  return (
    <CollapsibleSection
      title="VNC"
      icon="navVnc"
      open={open}
      onToggle={onToggle}
      actions={
        <Button
          size="sm"
          variant="primary"
          icon="plug"
          disabled={!canConnect}
          title={canConnect ? undefined : t('map.menu.noVncPermission')}
          onClick={() => onConnect({ id: node.id, name: node.name })}
        >
          {t('vnc.connect')}
        </Button>
      }
    >
      {vnc ? (
        <>
          {/* Итог зондирования внутри раздела, а не под заголовком: строка,
              видная у свёрнутого, удваивала бы высоту всего свёрнутого. */}
          <ProbeSummary vnc={vnc} />

          {/*
            `minmax(0, 1fr)` на треке и `w-full min-w-0` на полях: иначе
            колонку растягивает список кодировок, у которого самый длинный
            пункт — «Tight — лучшее сжатие рабочего стола».

            Панель от этого перестала уезжать вбок, но не до конца: сам
            выбранный пункт в списке шире поля, и на телефоне остаётся
            36 px переполнения. Чинится это уже не разметкой, а подписями
            пунктов — они писались под большой экран. Записано в `BACKLOG.md`.
          */}
          <div className="grid grid-cols-[86px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 text-sm">
            <label className="text-dim" htmlFor={`vnc-port-${node.id}`}>
              {t('agents.probe.port')}
            </label>
            {/*
              Ключ включает узел и сам порт, и это не украшение.

              Поле неуправляемое намеренно: правку принимает потеря фокуса,
              а не каждая нажатая цифра, — иначе порт «5900» по дороге успел
              бы сохраниться как «5», «59» и «590». Но неуправляемое поле
              читает `defaultValue` один раз, при создании, а раздел VNC
              при переходе на соседний узел не пересоздаётся. Пока параметры
              соседа грузились, поле успевало исчезнуть и появиться заново —
              и всё выглядело исправным; а на узле, который уже смотрели,
              ответ приходит из кэша сразу, исчезнуть поле не успевает,
              и в нём остаётся порт предыдущей машины. Списки рядом при этом
              показывают правильное — то есть панель уверенно врёт одним
              полем из четырёх.

              Ключ заставляет поле родиться заново, как только сменился узел
              или сохранённый порт.
            */}
            <input
              key={`${node.id}:${vnc.port}`}
              id={`vnc-port-${node.id}`}
              type="number"
              min={1}
              max={65535}
              defaultValue={vnc.port}
              disabled={!canWrite}
              onBlur={(event) => {
                const port = Number(event.target.value);

                if (port === vnc.port) {
                  return;
                }

                if (port >= 1 && port <= 65535) {
                  update.mutate({ port });
                  return;
                }

                // Негодный порт не сохраняем — и не оставляем на экране:
                // поле, показывающее то, чего нет в настройках, ничем
                // не отличается от поля, показывающего чужой узел.
                event.target.value = String(vnc.port);
              }}
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 font-mono text-sm"
            />

            <label className="text-dim" htmlFor={`vnc-kind-${node.id}`}>
              {t('vnc.server')}
            </label>
            <select
              id={`vnc-kind-${node.id}`}
              value={vnc.serverKind}
              disabled={!canWrite}
              onChange={(event) =>
                update.mutate({ serverKind: event.target.value as VncServerKind })
              }
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 text-sm"
            >
              {VNC_SERVER_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(KIND_LABEL_KEYS[kind])}
                </option>
              ))}
            </select>

            <label className="text-dim" htmlFor={`vnc-encoding-${node.id}`}>
              {t('vnc.encoding')}
            </label>
            <select
              id={`vnc-encoding-${node.id}`}
              value={vnc.preferEncoding}
              disabled={!canWrite}
              onChange={(event) =>
                update.mutate({ preferEncoding: event.target.value as VncEncodingPreference })
              }
              className="h-8 w-full min-w-0 rounded-xs border border-line bg-bg px-2 text-sm"
            >
              {VNC_ENCODING_PREFERENCES.map((encoding) => (
                <option key={encoding} value={encoding}>
                  {t(ENCODING_LABEL_KEYS[encoding])}
                </option>
              ))}
            </select>

            <span className="text-dim">{t('vnc.mode')}</span>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={vnc.viewOnly}
                disabled={!canWrite}
                onChange={(event) => update.mutate({ viewOnly: event.target.checked })}
              />
              {t('vnc.viewOnly')}
            </label>
          </div>

          {canManageCredentials ? <NodeCredentials nodeId={node.id} /> : null}

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              icon="refresh"
              disabled={probe.isPending}
              onClick={() => probe.mutate()}
            >
              {probe.isPending ? t('agents.probe.testing') : t('vnc.probeServer')}
            </Button>
            {probe.error ? (
              <span className="text-xs text-down">{errorText(probe.error)}</span>
            ) : null}
          </div>
        </>
      ) : (
        <p className="text-xs text-dim">{t('vnc.loadingSettings')}</p>
      )}
    </CollapsibleSection>
  );
}

/**
 * Итог зондирования одной строкой.
 *
 * Разделены три состояния, потому что чинятся они по-разному: «не проверяли»,
 * «проверили и не достучались» и «проверили, всё нашли». Общее «нет данных»
 * не отличило бы первое от второго.
 */
function ProbeSummary({ vnc }: { vnc: NodeVnc }): JSX.Element {
  const { t } = useTranslation();
  const language = currentLanguage();

  if (vnc.lastProbeAt === null) {
    return <p className="text-xs text-dim">{t('vnc.notProbedYet')}</p>;
  }

  if (vnc.lastProbeError !== null) {
    return (
      <p className="text-xs text-down">
        {messageText(vnc.lastProbeError, t)}{' '}
        <span className="text-dim">({formatMoment(vnc.lastProbeAt, language)})</span>
      </p>
    );
  }

  const kind =
    vnc.detectedKind === null ? t('vnc.serverNotIdentified') : t(KIND_LABEL_KEYS[vnc.detectedKind]);

  return (
    <p className="text-xs text-dim">
      {kind}
      {vnc.rfbVersion ? `, RFB ${vnc.rfbVersion}` : ''}
      {vnc.supportsFileTransfer === true ? t('vnc.hasFileTransfer') : ''}
      <span className="text-dim">
        {' '}
        · {t('vnc.probedAt', { at: formatMoment(vnc.lastProbeAt, language) })}
      </span>
    </p>
  );
}
